import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select } from '@/components/ui';
import { createContact, updateContact } from '@/data/client';
import { useMembers } from '@/data/hooks';
import type { Contact, ContactInput } from '@/data/types';
import { FormError, Modal } from './Modal';

/* Create or edit a contact. When created from a company page, `companyId` links
   it there; otherwise the server links it by email domain if a company owns it. */
export function ContactFormModal({ contact, companyId, onClose, stay }: {
  contact?: Contact; companyId?: string; onClose: () => void; stay?: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { ownerOptions } = useMembers();
  const [f, setF] = useState<ContactInput>({
    firstName: contact?.firstName ?? '', lastName: contact?.lastName ?? '', email: contact?.email ?? '',
    phone: contact?.phone ?? '', title: contact?.title ?? '', ownerUserId: contact?.ownerUserId ?? undefined,
  });
  const set = (k: keyof ContactInput) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));
  const empty = !f.firstName?.trim() && !f.lastName?.trim() && !f.email?.trim() && !f.phone?.trim();

  const save = useMutation({
    mutationFn: () => (contact ? updateContact(contact.id, f) : createContact({ ...f, companyId })),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact', saved.id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
      if (companyId) void qc.invalidateQueries({ queryKey: ['company', companyId] });
      if (saved.companyId) void qc.invalidateQueries({ queryKey: ['company', saved.companyId] });
      onClose();
      if (!contact && !stay) navigate(`/contacts/${saved.id}`);
    },
  });

  return (
    <Modal
      title={contact ? 'Edit contact' : 'New contact'}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || empty}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </>}
    >
      <form className="crm-form" onSubmit={(e) => { e.preventDefault(); if (!empty) save.mutate(); }}>
        <div className="crm-grid2">
          <Input label="First name" autoFocus value={f.firstName ?? ''} onChange={set('firstName')} />
          <Input label="Last name" value={f.lastName ?? ''} onChange={set('lastName')} />
          <Input label="Email" type="email" value={f.email ?? ''} onChange={set('email')} />
          <Input label="Phone" type="tel" value={f.phone ?? ''} onChange={set('phone')} />
        </div>
        <Input label="Title" placeholder="e.g. Owner, Marketing Manager" value={f.title ?? ''} onChange={set('title')} />
        <Select label="Owner" options={ownerOptions} placeholder="Choose owner" value={f.ownerUserId ?? ''} onChange={set('ownerUserId')} />
        <FormError error={save.error} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
